import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './UserDashboard.css';

function UserDashboard() {
  const { user, isAuthenticated, fetchWithAuth, logout } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [lamps, setLamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [bio, setBio] = useState('');
  const [selectedLamp, setSelectedLamp] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    fetchProfile();
    fetchLamps();
  }, [isAuthenticated]);

  const fetchProfile = async () => {
    try {
      const response = await fetchWithAuth('/api/user/profile');
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Profil geladen:', data);
        console.log('📷 Profilbild-URL:', data.profileImage);
        setProfile(data);
        setBio(data.bio || '');
        setSelectedLamp(data.favoriteLamp?._id || '');
        setImagePreview(data.profileImage || null);
      } else {
        setError('Fehler beim Laden des Profils');
        console.error('❌ Profil-API Error:', response.status);
      }
    } catch (err) {
      setError(err.message);
      console.error('❌ Profil-Fehler:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLamps = async () => {
    try {
      const response = await fetchWithAuth('/api/user/lamps');
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Lampen geladen:', data.length);
        setLamps(data);
      } else {
        console.error('Fehler beim Laden der Lampen:', response.status);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Lampen:', err);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await fetchWithAuth('/api/user/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bio,
          favoriteLamp: selectedLamp || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setSuccess('✅ Profil erfolgreich gespeichert!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Fehler beim Speichern');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Bild zu groß (max. 5MB)');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleUploadImage = async () => {
    if (!imageFile) return;

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('profileImage', imageFile);

      const response = await fetchWithAuth('/api/user/profile/image', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setProfile({ ...profile, profileImage: data.profileImage });
        setImagePreview(data.profileImage);
        setImageFile(null);
        setSuccess('✅ Profilbild hochgeladen!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Fehler beim Hochladen');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!window.confirm('Profilbild wirklich löschen?')) return;

    try {
      const response = await fetchWithAuth('/api/user/profile/image', {
        method: 'DELETE'
      });

      if (response.ok) {
        setProfile({ ...profile, profileImage: null });
        setImagePreview(null);
        setImageFile(null);
        setSuccess('✅ Profilbild gelöscht');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <div className="dashboard-loading">⏳ Lädt...</div>;
  }

  if (!profile) {
    return <div className="dashboard-error">Profil nicht gefunden</div>;
  }

  return (
    <div className="user-dashboard">
      <div className="dashboard-header">
        <h1>👤 Mein Profil</h1>
        <button className="back-btn" onClick={() => navigate('/')}>← Zurück</button>
      </div>

      {error && <div className="error-msg">{error}</div>}
      {success && <div className="success-msg">{success}</div>}

      <div className="dashboard-grid">
        {/* Profilbild Section */}
        <div className="profile-card">
          <h2>📷 Profilbild</h2>
          <div className="image-upload-section">
            <div className="image-preview">
              {imagePreview ? (
                <img src={imagePreview} alt="Profil" />
              ) : (
                <div className="no-image">
                  <span>👤</span>
                  <p>Kein Bild</p>
                </div>
              )}
            </div>
            
            <div className="image-controls">
              <label className="file-input-label">
                📁 Bild auswählen
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="file-input"
                />
              </label>
              
              {imageFile && (
                <button
                  onClick={handleUploadImage}
                  disabled={uploading}
                  className="upload-btn"
                >
                  {uploading ? '⏳ Hochladen...' : '⬆️ Hochladen'}
                </button>
              )}
              
              {profile.profileImage && !imageFile && (
                <button onClick={handleDeleteImage} className="delete-img-btn">
                  🗑️ Löschen
                </button>
              )}
            </div>
            <p className="image-hint">JPG, PNG, GIF oder WebP (max. 5MB)</p>
          </div>
        </div>

        {/* Account Info */}
        <div className="profile-card">
          <h2>📋 Account-Info</h2>
          <div className="info-list">
            <div className="info-item">
              <label>Username:</label>
              <span>{profile.username}</span>
            </div>
            <div className="info-item">
              <label>E-Mail:</label>
              <span>{profile.email}</span>
            </div>
            <div className="info-item">
              <label>Rolle:</label>
              <span className="role-badge">{profile.roles?.join(', ')}</span>
            </div>
            <div className="info-item">
              <label>Mitglied seit:</label>
              <span>{new Date(profile.createdAt).toLocaleDateString('de-DE')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bio & Lieblingslampe Form */}
      <form onSubmit={handleSaveProfile} className="profile-form">
        <div className="form-grid">
          {/* Bio */}
          <div className="form-section">
            <h2>✍️ Über mich / Motto</h2>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Erzähl etwas über dich oder dein Grow-Motto..."
              maxLength="500"
              rows="5"
            />
            <span className="char-count">{bio.length}/500</span>
          </div>

          {/* Lieblingslampe */}
          <div className="form-section">
            <h2>💡 Meine Lieblingslampe</h2>
            <select
              value={selectedLamp}
              onChange={(e) => setSelectedLamp(e.target.value)}
              className="lamp-select"
            >
              <option value="">-- Keine Lampe ausgewählt --</option>
              {lamps && lamps.length > 0 ? (
                lamps.map((lamp) => (
                  <option key={lamp._id} value={lamp._id}>
                    {lamp.name}
                  </option>
                ))
              ) : (
                <option disabled>Keine Lampen verfügbar</option>
              )}
            </select>
            
            {profile.favoriteLamp && (
              <div className="selected-lamp-preview">
                {profile.favoriteLamp.imageUrl && (
                  <img
                    src={profile.favoriteLamp.imageUrl}
                    alt={profile.favoriteLamp.name}
                  />
                )}
                <div>
                  <strong>{profile.favoriteLamp.name}</strong>
                  <p>{profile.favoriteLamp.shop}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <button type="submit" className="save-btn">
          💾 Bio & Lieblingslampe speichern
        </button>
      </form>

      {/* Logout */}
      <div className="logout-section">
        <button onClick={logout} className="logout-btn">
          🚪 Abmelden
        </button>
      </div>
    </div>
  );
}

export default UserDashboard;
